import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Search, Check } from "lucide-react";
import { cn } from "../../lib/utils/utils";

export function SearchableSelect({
  options = [],
  value = "",
  onChange,
  placeholder = "Select an option...",
  className
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  const selectedOption = options.find((opt) => String(opt.id) === String(value));
  const filteredOptions = options.filter((opt) =>
    opt.name.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectOption = (id) => {
    onChange(String(id));
    setIsOpen(false);
    setSearch("");
  };

  return (
    <div ref={containerRef} className={cn("relative w-full", className)}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex h-[48px] w-full cursor-pointer items-center justify-between rounded-2xl border border-slate-200 bg-white/90 px-3 py-2 text-sm transition-all focus-within:ring-2 focus-within:ring-primary/20 hover:border-slate-300",
          isOpen ? "ring-2 ring-primary/20 border-primary/50" : ""
        )}
      >
        <span className={selectedOption ? "text-panel-ink font-medium" : "text-panel-muted"}>
          {selectedOption ? selectedOption.name : placeholder}
        </span>
        <div className="flex items-center ml-2 text-slate-400">
          <ChevronDown
            size={18}
            className={cn("transition-transform duration-200", isOpen ? "rotate-180" : "")}
          />
        </div>
      </div>

      {isOpen && (
        <div className="absolute top-[calc(100%+8px)] z-[60] w-full rounded-2xl border border-slate-100 bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-200">
          <div className="p-3 border-b border-slate-50">
            <div className="relative flex items-center">
              <Search className="absolute left-3 text-slate-400" size={16} />
              <input
                autoFocus
                className="w-full rounded-xl bg-slate-50 border-none px-10 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/10"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
          <div className="max-h-[240px] overflow-y-auto p-1 custom-scrollbar">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const isSelected = String(opt.id) === String(value);
                return (
                  <div
                    key={opt.id}
                    onClick={() => selectOption(opt.id)}
                    className={cn(
                      "flex items-center justify-between rounded-xl px-3 py-2.5 text-sm cursor-pointer transition-all",
                      isSelected
                        ? "bg-primary/5 text-primary font-semibold"
                        : "hover:bg-slate-50 text-panel-ink"
                    )}
                  >
                    <span>{opt.name}</span>
                    {isSelected && <Check size={16} className="text-primary" />}
                  </div>
                );
              })
            ) : (
              <div className="p-4 text-center text-sm text-panel-muted">
                No results found.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
